import { createError, useRequestURL } from '#app'
import { useQuery } from '@tanstack/vue-query'
import { provideAnalyticsOrganizationFragment } from '~/composables/use-analytics-script'
import { provideLocalizationOrganizationFragment } from '~/composables/use-localization'
import { provideLoginModalOrganizationFragment } from '~/composables/use-login-modal-organization'
import { provideOrganizationContextFragment } from '~/composables/use-organization-context'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import type { OrganizationContextQueryQuery } from '~/resources/graphql/graphql'
import { computed } from 'vue'
import { useSelectedLanguage } from '~/composables/use-selected-language'

type OrganizationContextOrganization = NonNullable<
    OrganizationContextQueryQuery['organization']
>

export async function createOrganizationContext() {
    const selectedLanguage = useSelectedLanguage()
    const requestUrl = useRequestURL()
    const query = useRpQuery({ orgLevel: true })
    const { data, suspense } = useQuery({
        queryKey: [
            'OrganizationContextQuery',
            requestUrl.hostname,
            selectedLanguage
        ],
        placeholderData: previousData => previousData,
        queryFn: () =>
            query(
                graphql(/* GraphQL */ `
                    query OrganizationContextQuery($language: Language!) {
                        organization {
                            ...UseAnalyticsScriptOrganizationFragment
                            ...UseLocalizationOrganizationFragment
                            ...UseLoginModalOrganizationFragment
                            ...UseOrganizationContextFragment
                        }
                    }
                `),
                {
                    language: selectedLanguage.value
                }
            )
    })
    const organization = computed<OrganizationContextOrganization | null>(
        () => data.value?.organization ?? null
    )

    // Scope cached organization data to the tenant hostname and selected language
    // so context updates cannot reuse another tenant or locale as the active result.
    provideAnalyticsOrganizationFragment(organization)
    provideLocalizationOrganizationFragment(organization)
    provideLoginModalOrganizationFragment(organization)
    provideOrganizationContextFragment(organization)

    await suspense()
    if (organization.value === null) {
        throw createError({
            statusCode: 404,
            // Organization context is required before layouts run because layout
            // queries choose translated storefront data from the active locale.
            statusMessage: 'Page Not Found'
        })
    }
}
